import { Link, useRouterState } from '@tanstack/react-router';
import { LogOut, MoreHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { signOut, useSession } from '../../lib/auth-client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { InboxBadge, useInboxCount } from './InboxBadge';
import { isActivePath, PRIMARY_NAV, SECONDARY_NAV } from './nav';
import { ThemeToggle } from './ThemeToggle';

/** Fixed bottom navigation for phones/tablets (hidden from lg up). */
export function MobileTabBar() {
  const currentPath = useRouterState().location.pathname;
  const inboxCount = useInboxCount();
  const [moreOpen, setMoreOpen] = useState(false);
  const { data: session } = useSession();

  // Close the sheet after navigating
  // biome-ignore lint/correctness/useExhaustiveDependencies: run on path change
  useEffect(() => setMoreOpen(false), [currentPath]);

  const moreActive = !PRIMARY_NAV.some((t) => isActivePath(currentPath, t.to));

  return (
    <>
      <nav
        aria-label="Huvudmeny"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden"
      >
        <ul className="mx-auto grid max-w-md grid-cols-4">
          {PRIMARY_NAV.map((item) => {
            const Icon = item.icon;
            const active = isActivePath(currentPath, item.to);
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'relative flex h-15 flex-col items-center justify-center gap-1 text-[11px] font-medium',
                    active ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-7 w-12 items-center justify-center rounded-full transition-colors',
                      active && 'bg-primary/12 text-primary',
                    )}
                  >
                    <Icon className="size-5" />
                  </span>
                  {item.label}
                  {item.badge === 'inbox' && (
                    <InboxBadge count={inboxCount} className="absolute top-1 left-1/2 ml-3" />
                  )}
                </Link>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              className={cn(
                'flex h-15 w-full flex-col items-center justify-center gap-1 text-[11px] font-medium',
                moreActive ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'flex h-7 w-12 items-center justify-center rounded-full',
                  moreActive && 'bg-primary/12 text-primary',
                )}
              >
                <MoreHorizontal className="size-5" />
              </span>
              Mer
            </button>
          </li>
        </ul>
      </nav>

      <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Mer</DialogTitle>
            <DialogDescription className="sr-only">Fler sidor i Budgie</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {SECONDARY_NAV.map((section) => (
              <div key={section.label}>
                <p className="mb-1 px-1 text-xs font-medium text-muted-foreground">
                  {section.label}
                </p>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cn(
                        'flex h-11 items-center gap-3 rounded-lg px-3 text-base font-medium',
                        isActivePath(currentPath, item.to)
                          ? 'bg-muted text-foreground'
                          : 'text-foreground hover:bg-muted',
                      )}
                    >
                      <Icon className="size-5 text-muted-foreground" />
                      {item.label}
                      {item.badge === 'inbox' && (
                        <InboxBadge count={inboxCount} className="ml-auto" />
                      )}
                    </Link>
                  );
                })}
              </div>
            ))}
            <div>
              <p className="mb-1 px-1 text-xs font-medium text-muted-foreground">Utseende</p>
              <ThemeToggle />
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="truncate text-sm text-muted-foreground">{session?.user?.name}</span>
              <button
                type="button"
                onClick={async () => {
                  await signOut();
                  window.location.href = '/login';
                }}
                className="flex h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-muted-foreground hover:bg-muted"
              >
                <LogOut className="size-4" />
                Logga ut
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
